import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import useCruiseInclusions, { InclusionListSortField } from '~/components/Cruises/context/useCruiseInclusions';
import { CruiseInclusion } from '~/components/Cruises/pages/Inclusions/types';

import useAppNavigation from '~/hooks/useAppNavigation';

import { formatDateShort } from '~/services/TimeService';
import inclusionsService from '~/services/cruises/InclusionsService';

import InclusionSearchForm from './InclusionSearchForm';

const INCLUSION_TYPE = {
  STANDARD: 'Standard',
  LUX_PLUS: 'Lux Plus',
  EVERGREEN: 'Evergreen',
};

const formatInclusionDate = (inclusion: CruiseInclusion, value?: string | null) =>
  inclusion.type === 'EVERGREEN' || !value ? '-' : formatDateShort(value);

export default function InclusionList() {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteButton, setDeleteButton] = useState(null);

  const {
    inclusions,
    page,
    total,
    recordsPerPage,
    paginate,
    getInclusionList,
    isLoading,
    filters,
    setFilters,
    sort,
    setSort,
    activeTab,
    setActiveTab,
  } = useCruiseInclusions();
  const { push } = useAppNavigation();

  const sortModel: GridSortModel = useMemo(() => (sort ? [{ field: sort.field, sort: sort.direction }] : []), [sort]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const next = model[0];

      if (!next?.sort) {
        // DataGrid’s third click clears sort; cycle back to ascending on the same column.
        const field = next?.field || sort?.field;
        if (field) {
          setSort({ field: field as InclusionListSortField, direction: 'asc' });
          return;
        }

        return;
      }

      if (next.field === 'startDate' || next.field === 'endDate') {
        setSort({ field: next.field, direction: next.sort });
      }
    },
    [setSort, sort],
  );

  const handleDeleteBanner = useCallback(
    async (id: string) => {
      try {
        await inclusionsService.deleteById(id);
        enqueueSnackbar('Inclusion deleted', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Error deleting Inclusion', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const actionColumn = useCallback(
    (cell, row) => (
      <Box>
        {deleteButton !== row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                push(`/cruises/inclusions/edit/${row.id}/items`);
              }}
            >
              Edit
            </Button>

            <Button
              variant="text"
              size="small"
              color="error"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(row.id);
              }}
            >
              Delete
            </Button>
          </Box>
        )}

        {deleteButton === row.id && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await handleDeleteBanner(row.id);
                getInclusionList();
              }}
            >
              Confirm
            </Button>

            <Button
              variant="text"
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDeleteButton(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    ),
    [deleteButton, handleDeleteBanner, getInclusionList, push],
  );

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Title',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      display: 'flex',
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => INCLUSION_TYPE[params.value],
      display: 'flex',
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatInclusionDate(params.row, params.value),
      display: 'flex',
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 2,
      renderCell: (params) => formatInclusionDate(params.row, params.value),
      display: 'flex',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 180,
      sortable: false,
      renderCell: (params) => actionColumn(params.value, params.row),
      display: 'flex',
    },
  ];

  return (
    <Box mt={1}>
      <InclusionSearchForm isLoading={isLoading} filters={filters} onSearch={setFilters} />

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mt: 2 }}>
        <Tab label="Live" value="live" />
        <Tab label="Inactive" value="inactive" />
      </Tabs>

      <Stack mt={3} width="100%" direction="row" justifyContent="flex-end">
        <Button component={Link} to="/cruises/inclusions/create" variant="text" size="small">
          Add New Inclusion
        </Button>
      </Stack>

      <DataGrid
        loading={isLoading}
        rows={inclusions || []}
        columns={columns}
        pagination
        paginationModel={{ page: page - 1, pageSize: recordsPerPage }}
        pageSizeOptions={[10]}
        getRowId={(row: CruiseInclusion) => row.id}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowCount={total}
        onPaginationModelChange={({ page }) => paginate(page + 1)}
        slots={{ pagination: GridPagination }}
        autoHeight
      />
    </Box>
  );
}
